/**
 * Personal bot-defaults card (personal Settings). A trader's overrides on top
 * of the org baseline; renders only when the trader has bot access. Saves via
 * PATCH /api/trader/me/bot-defaults, then refreshMe() to update the session.
 */
import { useEffect, useState } from 'react';
import { Bot } from 'lucide-react';
import { apiFetch, useAuthStore } from '@/stores/authStore';
import type { BotDefaults, BotKindConfig } from '@/lib/bots/botDefaults';
import { BotDefaultsEditor, cloneByKind } from '@/components/bots/BotDefaultsEditor';

type ByKind = Partial<Record<string, BotKindConfig>>;

export function BotDefaultsPanel() {
  const botAccess = useAuthStore((s) => s.user?.botAccess ?? false);
  const stored = useAuthStore((s) => s.user?.botDefaults);
  const refreshMe = useAuthStore((s) => s.refreshMe);
  const [byKind, setByKind] = useState<ByKind>(() => cloneByKind(stored?.byKind));
  const [dirty, setDirty] = useState(false);
  // A rejected field never reaches `byKind`, so Save would otherwise stay
  // enabled on the strength of an earlier valid edit and quietly save something
  // other than what the trader is looking at.
  const [editorValid, setEditorValid] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed from the store when it changes, unless there are unsaved edits.
  useEffect(() => {
    if (!dirty) setByKind(cloneByKind(stored?.byKind));
  }, [stored, dirty]);

  if (!botAccess) return null;

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const body: BotDefaults = { ...(stored ?? {}), byKind };
      const res = await apiFetch('/api/trader/me/bot-defaults', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ botDefaults: body }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? 'Save failed');
        return;
      }
      // refreshMe FIRST, then clear dirty. Clearing dirty before refreshMe
      // resolves would let the re-seed effect fire against the still-stale store
      // and flicker the editor back to pre-save values mid-await.
      await refreshMe();
      setDirty(false);
      setSaved(true);
    } catch {
      setError('Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card border-border rounded-lg border p-5">
      <h2 className="mb-1 flex items-center gap-2 text-base font-medium">
        <Bot size={16} className="text-muted-foreground" /> Bot defaults
      </h2>
      <p className="text-muted-foreground mb-3 text-xs">
        Your personal defaults for deploying bots. These override the org
        baseline; anything left blank inherits it.
      </p>
      <BotDefaultsEditor
        byKind={byKind}
        scope="personal"
        onValidityChange={setEditorValid}
        onChange={(next) => {
          setByKind(next);
          setDirty(true);
          setSaved(false);
        }}
      />
      <div className="mt-3 flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving || !dirty || !editorValid}
          className="bg-primary text-primary-foreground rounded px-3 py-1.5 text-xs font-medium disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save defaults'}
        </button>
        {saved && !dirty && <span className="text-xs text-emerald-500">Saved</span>}
        {error && <span className="text-xs text-red-400">{error}</span>}
      </div>
    </div>
  );
}
